import * as React from "react";
import { useAction } from "wasp/client/operations";
import { getAdHocRecipeCostQuote } from "wasp/client/operations";
import { useLocation } from "react-router-dom";

import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Textarea } from "../../components/ui/textarea";
import { Separator } from "../../components/ui/separator";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";

type SupportedCurrency = "BBD" | "USD";

type Row = {
  name: string;
  qty: number;
  unit: string; // g | kg | ml | L | tsp | tbsp | cup | each
  // direct price mode (optional)
  price?: number; // price per priceUnit
  priceUnit?: string;
  currency?: SupportedCurrency;

  // packaging mode (optional) — derive price/priceUnit
  packQty?: number; // e.g., 1 case
  packLabel?: string; // "case" (display only)
  packSize?: number; // e.g., 50
  packSizeUnit?: string; // e.g., "kg"
  packPrice?: number; // total price of the pack (in currency)
};

export default function AdHocCostingPage() {
  const location = useLocation();
  const isPublic = React.useMemo(() => {
    const path = location.pathname || "";
    if (!path.startsWith("/ims/costing/adhoc")) return false;
    const params = new URLSearchParams(location.search || "");
    return !!(params.get("access") || params.get("token"));
  }, [location.pathname, location.search]);

  const [rows, setRows] = React.useState<Row[]>([
    // starter example row (you can delete)
    {
      name: "Potatoes",
      qty: 10,
      unit: "kg",
      packQty: 1,
      packLabel: "case",
      packSize: 50,
      packSizeUnit: "kg",
      packPrice: 100,
      currency: "BBD",
    },
  ]);
  const [servings, setServings] = React.useState<number>(10);
  const [currency, setCurrency] = React.useState<SupportedCurrency>("BBD");
  const [title, setTitle] = React.useState<string>("Ad-hoc IMS Recipe");
  const [paste, setPaste] = React.useState<string>("");
  const [result, setResult] = React.useState<any>(null);
  const [error, setError] = React.useState<string | null>(null);

  const run = useAction(getAdHocRecipeCostQuote);
  const [loading, setLoading] = React.useState(false);

  // Add noindex meta in public mode to discourage crawling.
  React.useEffect(() => {
    if (!isPublic) return;
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    return () => {
      try { document.head.removeChild(meta); } catch {}
    };
  }, [isPublic]);

  // Minimal client-side unit conversion (mirrors server).
  function convertUnits(quantity: number, fromUnit: string, toUnit: string): number | null {
    const rates: Record<string, { baseUnit: string; multiplier: number }> = {
      g: { baseUnit: 'g', multiplier: 1 }, gram: { baseUnit: 'g', multiplier: 1 }, kg: { baseUnit: 'g', multiplier: 1000 }, kilogram: { baseUnit: 'g', multiplier: 1000 }, oz: { baseUnit: 'g', multiplier: 28.35 }, lb: { baseUnit: 'g', multiplier: 453.592 },
      ml: { baseUnit: 'ml', multiplier: 1 }, milliliter: { baseUnit: 'ml', multiplier: 1 }, l: { baseUnit: 'ml', multiplier: 1000 }, liter: { baseUnit: 'ml', multiplier: 1000 }, tsp: { baseUnit: 'ml', multiplier: 4.928 }, tbsp: { baseUnit: 'ml', multiplier: 14.787 }, cup: { baseUnit: 'ml', multiplier: 240 },
      each: { baseUnit: 'each', multiplier: 1 }, count: { baseUnit: 'each', multiplier: 1 },
    };
    const from = rates[(fromUnit || '').toLowerCase()];
    const to = rates[(toUnit || '').toLowerCase()];
    if (!from || !to) return null;
    if (from.baseUnit !== to.baseUnit) return null;
    return (quantity * from.multiplier) / to.multiplier;
  }

  function calculateClientSide(items: ReturnType<typeof deriveForServer>[], servings_: number, curr: SupportedCurrency) {
    const FX_USD_BBD = Number((import.meta as any).env?.VITE_FX_USD_BBD) || 2.0;
    const BBD_TO_USD_RATE = 1 / FX_USD_BBD;
    const line_items: any[] = [];
    const meta = { missing_prices: [] as string[], missing_conversions: [] as string[], stale_prices: [] as string[], used_exchange_rate: null as Record<string, number> | null, version: "v0.1" as const };
    for (const item of items) {
      if (!item.price || !item.priceUnit) { meta.missing_prices.push(`${item.name}`); continue; }
      const recipeUnit = (item.unit || '').toLowerCase();
      const priceUnit = (item.priceUnit || '').toLowerCase();
      const requiredQty = Number(item.qty || 0);
      const qtyInPriceUnit = convertUnits(requiredQty, recipeUnit, priceUnit);
      if (qtyInPriceUnit === null) { meta.missing_conversions.push(`${item.name}: ${recipeUnit}→${priceUnit}`); continue; }
      let cost = qtyInPriceUnit * Number(item.price);
      let priceCurrency = item.currency || curr;
      if (priceCurrency !== curr) {
        if (priceCurrency === 'BBD' && curr === 'USD') { cost *= BBD_TO_USD_RATE; meta.used_exchange_rate = { 'BBD→USD': BBD_TO_USD_RATE }; }
        else if (priceCurrency === 'USD' && curr === 'BBD') { cost /= BBD_TO_USD_RATE; meta.used_exchange_rate = { 'USD→BBD': 1 / BBD_TO_USD_RATE }; }
      }
      line_items.push({ name: item.name, qty_used: requiredQty, unit: recipeUnit, price_unit: priceUnit, price_per_unit: item.price, currency: curr, cost: parseFloat(cost.toFixed(4)), notes: `Converted ${requiredQty} ${recipeUnit} to ${qtyInPriceUnit.toFixed(4)} ${priceUnit}` });
    }
    const total_cost = line_items.reduce((s, li) => s + li.cost, 0);
    return {
      line_items,
      totals: { total_cost: parseFloat(total_cost.toFixed(2)), cost_per_serving: parseFloat((total_cost / servings_).toFixed(2)), servings: servings_, currency: curr },
      meta,
    };
  }

  function deriveForServer(r: Row) {
    const d = deriveUnitPrice(r);
    return { name: r.name, qty: toNum(r.qty, 0), unit: (r.unit || '').toLowerCase(), price: d.price, priceUnit: d.priceUnit, currency: d.currency };
  }

  async function handleCalculate() {
    setError(null);
    setLoading(true);
    try {
      const items = rows.map(deriveForServer);
      const payload = { title, servings: toNum(servings, 1), currency, items };
      const data = isPublic
        ? calculateClientSide(items, payload.servings, currency)
        : await run(payload as any);
      setResult(data);
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Failed to cost recipe.");
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  function addRow() {
    setRows((prev) => [...prev, { name: "", qty: 1, unit: "each", currency }]);
  }

  function removeRow(idx: number) {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  }

  function updateRow(idx: number, patch: Partial<Row>) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  // Derive price + priceUnit from packaging fields if provided
  function deriveUnitPrice(r: Row): {
    price?: number;
    priceUnit?: string;
    currency?: SupportedCurrency;
  } {
    if (r.packPrice != null && r.packSize && r.packSizeUnit) {
      const unitPrice = r.packPrice / r.packSize;
      return {
        price: round(unitPrice, 6),
        priceUnit: r.packSizeUnit.toLowerCase(),
        currency: r.currency || currency,
      };
    }
    // fallback to direct price fields if present
    if (r.price != null && r.priceUnit) {
      return {
        price: r.price,
        priceUnit: r.priceUnit.toLowerCase(),
        currency: r.currency || currency,
      };
    }
    return {};
  }

  // Quick paste helper: lines like
  // Potatoes, 10, kg, 1 case = 100 BBD (50 kg)
  // Tomato, 450, g, 5.2 BBD / kg
  function parsePaste() {
    if (!paste.trim()) return;
    const lines = paste
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    const parsed: Row[] = [];
    for (const line of lines) {
      const p = parseLooseLine(line, currency);
      if (p) parsed.push(p);
    }
    if (parsed.length > 0) setRows(parsed);
  }

  const hasGaps = !!result && (result.meta?.missing_prices?.length || result.meta?.missing_conversions?.length || result.meta?.stale_prices?.length)

  return (
    <div className="mx-auto max-w-7xl space-y-8 py-6 px-4 bg-coral-200">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold mx-auto">Ad-hoc Recipe Costing (IMS)</h1>
      </div>

      <Card>
        <CardHeader><CardTitle>How this works (super simple)</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-2">
          <ul className="list-disc pl-5 space-y-1">
            <li>Type your items: <em>name, quantity, unit</em> (e.g. <code>Potatoes, 10, kg</code>).</li>
            <li>Give a price EITHER by <strong>Packaging</strong> (<em>1 case, 50 kg, 100</em>) OR <strong>Direct Price</strong> (<em>5.2 per kg</em>).</li>
            <li>Click <strong>Calculate</strong> to see total and per-serving cost.</li>
          </ul>
          <div className="text-xs text-muted-foreground">
            Examples:<br/>
            <code>Potatoes, 10, kg, 1 case = 100 BBD (50 kg)</code><br/>
            <code>Tomato, 450, g, 5.2 BBD / kg</code>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recipe Meta</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Servings</Label>
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              value={servings}
              onChange={(e) =>
                setServings(Math.max(1, Number(e.target.value || 1)))
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label>Currency</Label>
            <Select
              value={currency}
              onValueChange={(v) => setCurrency(v as SupportedCurrency)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Currency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BBD">BBD</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Items</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Quick paste helper */}
          {/*<div className="space-y-2">
            <Label className="text-sm">Quick paste (optional)</Label>
            <Textarea
              rows={4}
              placeholder={`Examples:\nPotatoes, 10, kg, 1 case = 100 BBD (50 kg)\nTomato, 450, g, 5.2 BBD / kg`}
              value={paste}
              onChange={(e) => setPaste(e.target.value)}
            />
            <div className="flex gap-2">
              <Button variant="secondary" onClick={parsePaste}>
                Parse to rows
              </Button>
              <Button variant="outline" onClick={() => setPaste("")}>
                Clear
              </Button>
            </div>*/}
          {/*</div>*/}

          <div className="rounded-xl border">
            <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[15%]">Name</TableHead>
                                <TableHead className="w-[8%]">Qty</TableHead>
                                <TableHead className="w-[12%]">Unit</TableHead>
                                <TableHead className="w-[30%]">Packaging (optional)</TableHead>
                                <TableHead className="w-[25%]">Direct Price (optional)</TableHead>
                                <TableHead className="w-[10%]"> </TableHead>
                              </TableRow>
                            </TableHeader>
              <TableBody>
                {rows.map((r, i) => {
                  const derived = deriveUnitPrice(r);
                  return (
                    <TableRow key={i}>
                      <TableCell>
                        <Input
                          placeholder="Potatoes"
                          value={r.name}
                          onChange={(e) =>
                            updateRow(i, { name: e.target.value })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          inputMode="decimal"
                          placeholder="10"
                          value={r.qty ?? ""}
                          onChange={(e) =>
                            updateRow(i, { qty: toNum(e.target.value) })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          placeholder="kg"
                          value={r.unit || ""}
                          onChange={(e) =>
                            updateRow(i, { unit: e.target.value })
                          }
                        />
                      </TableCell>

                      {/* Packaging mode */}
                      <TableCell>
                        <div className="grid grid-cols-5 gap-2">
                          <Input
                            className="col-span-1"
                            placeholder="1"
                            type="number"
                            value={r.packQty ?? ""}
                            onChange={(e) =>
                              updateRow(i, { packQty: toNum(e.target.value) })
                            }
                          />
                          <Input
                            className="col-span-1"
                            placeholder="case"
                            value={r.packLabel ?? ""}
                            onChange={(e) =>
                              updateRow(i, { packLabel: e.target.value })
                            }
                          />
                          <Input
                            className="col-span-1"
                            placeholder="50"
                            type="number"
                            value={r.packSize ?? ""}
                            onChange={(e) =>
                              updateRow(i, { packSize: toNum(e.target.value) })
                            }
                          />
                          <Input
                            className="col-span-1"
                            placeholder="kg"
                            value={r.packSizeUnit ?? ""}
                            onChange={(e) =>
                              updateRow(i, { packSizeUnit: e.target.value })
                            }
                          />
                          <Input
                            className="col-span-1"
                            placeholder="100"
                            type="number"
                            value={r.packPrice ?? ""}
                            onChange={(e) =>
                              updateRow(i, { packPrice: toNum(e.target.value) })
                            }
                          />
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          e.g. <em>1 case, 50 kg, 100</em> → derives{" "}
                          {derived.price ? money(derived.price, currency) : "—"}{" "}
                          per {derived.priceUnit || "?"}
                        </p>
                      </TableCell>

                      {/* Direct price mode */}
                      <TableCell>
                        <div className="grid grid-cols-3 gap-2">
                          <Input
                            className="col-span-1"
                            placeholder="5.2"
                            type="number"
                            value={r.price ?? ""}
                            onChange={(e) =>
                              updateRow(i, { price: toNum(e.target.value) })
                            }
                          />
                          <Input
                            className="col-span-1"
                            placeholder="kg"
                            value={r.priceUnit ?? ""}
                            onChange={(e) =>
                              updateRow(i, { priceUnit: e.target.value })
                            }
                          />
                          <Select
                            value={r.currency || currency}
                            onValueChange={(v) =>
                              updateRow(i, { currency: v as SupportedCurrency })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="BBD">BBD</SelectItem>
                              <SelectItem value="USD">USD</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </TableCell>

                      <TableCell className="text-right">
                        <Button variant="ghost" onClick={() => removeRow(i)}>
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell colSpan={6}>
                    <Button variant="outline" onClick={addRow}>
                      + Add item
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground mx auto">
              Tip: leave price fields blank to see what the engine flags as
              missing.
            </div>
            <Button onClick={handleCalculate} disabled={loading}>
              {loading ? "Calculating…" : "Calculate"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {error && (
        <Alert variant="destructive">
          <AlertTitle>Couldn’t cost recipe</AlertTitle>
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}

      {!error && result && (
        <Card>
          <CardHeader>
            <CardTitle>Breakdown ({result.totals.currency})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Stat
                title="Total Cost"
                value={money(result.totals.total_cost, result.totals.currency)}
              />
              <Stat
                title="Per Serving"
                value={money(
                  result.totals.cost_per_serving,
                  result.totals.currency,
                )}
              />
              <Stat title="Servings" value={String(result.totals.servings)} />
            </div>

            {hasGaps && (
              <div className="space-y-2">
                {result.meta.missing_prices?.length > 0 && (
                  <Alert>
                    <AlertTitle className="text-sm font-medium">
                      Missing prices
                    </AlertTitle>
                    <AlertDescription className="text-xs">
                      {result.meta.missing_prices.join(", ")}
                    </AlertDescription>
                  </Alert>
                )}
                {result.meta.missing_conversions?.length > 0 && (
                  <Alert>
                    <AlertTitle className="text-sm font-medium">
                      Missing unit conversions
                    </AlertTitle>
                    <AlertDescription className="text-xs">
                      {result.meta.missing_conversions.join(", ")}
                    </AlertDescription>
                  </Alert>
                )}
                {result.meta.stale_prices?.length > 0 && (
                  <Alert>
                    <AlertTitle className="text-sm font-medium">
                      Stale prices (&gt;60d)
                    </AlertTitle>
                    <AlertDescription className="text-xs">
                      {result.meta.stale_prices.join(", ")}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            <Separator />
            <div className="rounded-xl border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[30%]">Ingredient</TableHead>
                    <TableHead className="w-[15%]">Qty</TableHead>
                    <TableHead className="w-[15%]">Unit</TableHead>
                    <TableHead className="w-[15%]">Price / Unit</TableHead>
                    <TableHead className="w-[15%]">Line Cost</TableHead>
                    <TableHead className="w-[10%]">Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.line_items.map((i: any) => (
                    <TableRow key={i.ingredientId}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {i.name}
                          {/* stale badge if meta mentions this id by name */}
                          {result.meta.stale_prices?.some((s: string) =>
                            s.includes(i.name),
                          ) && <Badge variant="secondary">Stale</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>{fmt(i.qty_used)}</TableCell>
                      <TableCell>{i.unit}</TableCell>
                      <TableCell>
                        {money(i.price_per_unit, result.totals.currency)}{" "}
                        <span className="text-muted-foreground">
                          /{i.price_unit}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">
                        {money(i.cost, result.totals.currency)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {i.notes || "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {result.meta.used_exchange_rate && (
              <p className="text-xs text-muted-foreground">
                Exchange rate:{" "}
                {Object.entries(result.meta.used_exchange_rate)
                  .map(([k, v]) => `${k}=${v}`)
                  .join(", ")}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* — helpers — */
function toNum(v: any, fallback = 0) {
  const n =
    typeof v === "number" ? v : parseFloat(String(v).replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) ? n : fallback;
}
function round(n: number, dp = 2) {
  const f = Math.pow(10, dp);
  return Math.round(n * f) / f;
}
function money(v: number, currency: SupportedCurrency) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(v);
  } catch {
    return `$${v.toFixed(2)} ${currency}`;
  }
}
function fmt(v: number) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(
    v,
  );
}

/** very forgiving line parser
 * Accepts forms like:
 *  - Potatoes, 10, kg, 1 case = 100 BBD (50 kg)
 *  - Tomato, 450, g, 5.2 BBD / kg
 *  - Flour, 1, kg, 2.5 / kg
 */
function parseLooseLine(
  line: string,
  defaultCurrency: SupportedCurrency,
): Row | null {
  // split first three tokens as name, qty, unit
  const parts = line.split(",").map((s) => s.trim());
  if (parts.length < 3) return null;
  const name = parts[0];
  const qty = toNum(parts[1], 0);
  const unit = (parts[2] || "").toLowerCase();
  if (!name || !qty || !unit) return null;

  // rest joined for price/pack parsing
  const tail = parts.slice(3).join(", ").trim();

  // pattern A: packaging "1 case = 100 BBD (50 kg)" or "1 case = 100 (50 kg)"
  const pack = tail.match(
    /(?:(\d+(?:\.\d+)?)\s*\w+\s*=)?\s*(\d+(?:\.\d+)?)\s*(BBD|USD)?\s*\(\s*(\d+(?:\.\d+)?)\s*([a-zA-Z]+)\s*\)/i,
  );
  if (pack) {
    const packPrice = toNum(pack[2]);
    const cur = (pack[3] as SupportedCurrency) || defaultCurrency;
    const packSize = toNum(pack[4]);
    const packSizeUnit = (pack[5] || "").toLowerCase();
    const packQty = pack[1] ? toNum(pack[1], 1) : 1;
    return {
      name,
      qty,
      unit,
      packQty,
      packLabel: "case",
      packSize,
      packSizeUnit,
      packPrice,
      currency: cur,
    };
  }

  // pattern B: direct price "5.2 BBD / kg" or "2.5 / kg"
  const direct = tail.match(/(\d+(?:\.\d+)?)\s*(BBD|USD)?\s*\/\s*([a-zA-Z]+)/i);
  if (direct) {
    const price = toNum(direct[1]);
    const cur = (direct[2] as SupportedCurrency) || defaultCurrency;
    const priceUnit = (direct[3] || "").toLowerCase();
    return { name, qty, unit, price, priceUnit, currency: cur };
  }

  // nothing else provided -> just the item
  return { name, qty, unit, currency: defaultCurrency };
}

function Stat({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="text-xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}
